import { useState, useEffect, useRef } from "react";
import { Tool, ProfessionalCategory } from "@/types/tool";
import MapSection from "@/components/MapSection";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";
import mapboxgl from 'mapbox-gl';

const MinimalEmbed = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isMapExpanded, setIsMapExpanded] = useState(true);
  const [tools, setTools] = useState<Tool[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    fetchTools();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchTools = async () => {
    try {
      const { data, error } = await supabase
        .from('professional_profiles')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching tools:', error);
        toast.error('Failed to load business profiles');
        return;
      }

      if (!data) {
        console.log('No data returned from query');
        return;
      }

      const mappedTools: Tool[] = data.map(profile => ({
        id: profile.id,
        name: profile.business_name,
        description: profile.bio || '',
        logo: profile.business_image_url || '/tools/installer.png',
        categories: profile.categories.filter((cat): cat is ProfessionalCategory => 
          ['installer', 'retailer', 'manufacturer', 'convention', 'rep'].includes(cat)
        ),
        tags: profile.categories || [],
        rating: profile.business_rating || 0,
        reviews: profile.business_total_ratings || 0,
        bookmarks: 0,
        featured: true,
        pricing: 'Quote-based',
        visitUrl: profile.business_website || '#',
        latitude: profile.lat || undefined,
        longitude: profile.lng || undefined,
        business_phone: profile.business_phone,
        address: profile.address ? `${profile.address}, ${profile.city}, ${profile.state}` : undefined
      }));

      setTools(mappedTools);
    } catch (error) {
      console.error('Error in fetchTools:', error);
      toast.error('Failed to load business profiles');
    }
  };

  const filteredTools = tools.filter((tool) => {
    if (!selectedCategory) return true;
    
    return tool.categories.some(category => 
      category.toLowerCase() === selectedCategory.toLowerCase()
    );
  });

  return (
    <div className="h-screen bg-white relative">
      <div className="absolute top-4 right-4 z-10">
        <a 
          href="https://fuzionsocial.ai" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block"
        >
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            List Your Business
            <ArrowUpRight className="w-4 h-4" />
          </Button>
        </a>
      </div>
      
      <div className="h-full">
        <div className="h-full rounded-xl overflow-hidden shadow-xl">
          <MapSection
            isMapExpanded={isMapExpanded}
            onMapExpandToggle={() => setIsMapExpanded(!isMapExpanded)}
            filteredTools={filteredTools}
            isEditing={false}
            latitude={null}
            longitude={null}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            mapRef={mapRef}
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
};

export default MinimalEmbed;